(() => {
  document.documentElement.classList.remove('no-js');

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const quantityText = (quantity) => {
    const qty = Number(quantity || 1);
    if (qty === 1) return '1 boca';
    if (qty >= 2 && qty <= 4) return `${qty} boce`;
    return `${qty} boca`;
  };

  // Reveal animations. Works for the long photograph as normal page scroll — no scroll hijacking.
  const initReveals = (root = document) => {
    const elements = qsa('.reveal:not([data-reveal-bound])', root);
    if (!elements.length) return;

    if (!reducedMotion && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, revealObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -45px 0px' });

      elements.forEach((element) => {
        element.dataset.revealBound = 'true';
        observer.observe(element);
      });
    } else {
      elements.forEach((element) => {
        element.dataset.revealBound = 'true';
        element.classList.add('is-visible');
      });
    }
  };

  // Header state.
  const header = qs('[data-legacy-header]');
  const syncHeader = () => {
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 18);
  };
  syncHeader();
  window.addEventListener('scroll', syncHeader, { passive: true });

  // Mobile menu.
  const drawer = qs('[data-mobile-drawer]');
  const openButton = qs('[data-menu-open]');
  const openMenu = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    openButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
  };
  const closeMenu = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    openButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
  };
  qsa('[data-menu-open]').forEach((button) => button.addEventListener('click', openMenu));
  qsa('[data-menu-close]').forEach((button) => button.addEventListener('click', closeMenu));
  qsa('[data-mobile-drawer] a').forEach((link) => link.addEventListener('click', closeMenu));

  // Product image gallery.
  const initGalleries = (root = document) => {
    qsa('[data-product-gallery]:not([data-gallery-bound])', root).forEach((gallery) => {
      gallery.dataset.galleryBound = 'true';
      const mainImage = qs('[data-main-product-image]', gallery);
      const thumbs = qsa('[data-product-thumb]', gallery);
      if (!mainImage || !thumbs.length) return;

      let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'));
      const activate = (index) => {
        activeIndex = (index + thumbs.length) % thumbs.length;
        const thumb = thumbs[activeIndex];
        if (thumb.dataset.src) mainImage.src = thumb.dataset.src;
        mainImage.alt = thumb.dataset.alt || '';
        thumbs.forEach((item) => item.setAttribute('aria-current', 'false'));
        thumb.setAttribute('aria-current', 'true');
      };

      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));

      let touchStartX = null;
      mainImage.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches?.[0]?.clientX ?? null;
      }, { passive: true });
      mainImage.addEventListener('touchend', (event) => {
        if (touchStartX === null || thumbs.length < 2) return;
        const endX = event.changedTouches?.[0]?.clientX ?? touchStartX;
        const distance = endX - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) < 45) return;
        activate(activeIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });
    });
  };

  const refreshBundle = (form, requestedQuantity) => {
    const group = qs('[data-bundle-group]', form);
    if (!group) return;

    const context = form.closest('.legacy-buy-v3__content, .legacy-product__info') || form.parentElement;
    const qtyInput = qs('input[name="quantity"]', form);
    const note = qs('[data-bundle-note]', form);
    const addPrice = qs('[data-add-price]', form);
    const unitMoney = qs('[data-unit-money]', form);
    const totalPrice = qs('[data-total-price]', context);
    const quantityLabel = qs('[data-quantity-label]', form);
    const selected = qs(`[data-qty="${requestedQuantity}"]`, group) || qs('[data-qty]', group);
    const quantity = selected?.dataset.qty || '1';
    const unitPrice = Number(group.dataset.unitPrice || 0);
    const currency = group.dataset.currency || 'EUR';
    const total = unitPrice * Number(quantity);
    const notes = {
      '1': group.dataset.note1 || '',
      '2': group.dataset.note2 || '',
      '5': group.dataset.note5 || '',
      '10': group.dataset.note10 || ''
    };

    qsa('[data-qty]', group).forEach((item) => {
      const isSelected = item.dataset.qty === quantity;
      item.classList.toggle('is-selected', isSelected);
      const radio = qs('input[type="radio"]', item);
      if (radio) radio.checked = isSelected;
    });

    if (qtyInput) qtyInput.value = quantity;
    if (quantityLabel) quantityLabel.textContent = quantityText(quantity);
    if (note) note.textContent = notes[quantity] || '';
    if (unitMoney && unitPrice) unitMoney.textContent = `${formatMoney(unitPrice, currency)} / boca`;
    if (totalPrice && total) totalPrice.textContent = formatMoney(total, currency);
    if (addPrice && total) addPrice.textContent = formatMoney(total, currency);
  };

  const initProductForms = (root = document) => {
    qsa('form[data-legacy-product-form]:not([data-product-form-bound])', root).forEach((form) => {
      form.dataset.productFormBound = 'true';
      const group = qs('[data-bundle-group]', form);

      if (group) {
        qsa('[data-qty]', group).forEach((card) => {
          card.addEventListener('click', () => refreshBundle(form, card.dataset.qty || '1'));
        });
        qsa('input[type="radio"]', group).forEach((radio) => {
          radio.addEventListener('change', () => refreshBundle(form, radio.value));
        });
        const checked = qs('input[type="radio"]:checked', group);
        refreshBundle(form, checked?.value || '1');
      }

      const variantSelect = qs('[data-variant-select]', form);
      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          const option = variantSelect.options[variantSelect.selectedIndex];
          const bundleGroup = qs('[data-bundle-group]', form);
          const unitPrice = Number(option?.dataset.price || 0);
          const currency = bundleGroup?.dataset.currency || window.Shopify?.currency?.active || 'EUR';
          if (bundleGroup && unitPrice) bundleGroup.dataset.unitPrice = String(unitPrice);
          if (bundleGroup) {
            const checked = qs('input[type="radio"]:checked', bundleGroup);
            refreshBundle(form, checked?.value || '1');
          } else if (unitPrice) {
            const context = form.closest('.legacy-buy-v3__content, .legacy-product__info') || form.parentElement;
            const totalPrice = qs('[data-total-price]', context);
            const addPrice = qs('[data-add-price]', form);
            if (totalPrice) totalPrice.textContent = formatMoney(unitPrice, currency);
            if (addPrice) addPrice.textContent = formatMoney(unitPrice, currency);
          }
        });
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = qs('[data-add-button]', form);
        if (!button || button.disabled) return;

        const original = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '<span>Dodavanje…</span>';

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Proizvod nije moguće dodati.');

          const cart = await fetch(`${rootUrl}cart.js`, {
            headers: { 'Accept': 'application/json' }
          }).then((res) => res.json());

          qsa('[data-cart-count]').forEach((count) => {
            count.textContent = cart.item_count;
            count.hidden = cart.item_count < 1;
          });
          showToast(`${quantityText(qs('input[name="quantity"]', form)?.value || 1)} dodano u košaricu.`);
        } catch (error) {
          showToast(error.message || 'Dogodila se greška. Pokušajte ponovno.');
        } finally {
          button.disabled = false;
          button.innerHTML = original;
          const checked = qs('[data-bundle-group] input[type="radio"]:checked', form);
          refreshBundle(form, checked?.value || '1');
        }
      });
    });
  };

  function showToast(message) {
    const toast = qs('#LegacyToast');
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');
    window.clearTimeout(window.__legacyToastTimer);
    window.__legacyToastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 2800);
  }

  const initCart = (root = document) => {
    qsa('[data-cart-form]:not([data-cart-bound])', root).forEach((form) => {
      form.dataset.cartBound = 'true';
      let updateTimer = null;
      let isUpdating = false;

      const submitUpdate = () => {
        if (isUpdating) return;
        isUpdating = true;
        form.classList.add('is-updating');
        // Native form submission keeps Shopify's cart logic authoritative and reliable.
        HTMLFormElement.prototype.submit.call(form);
      };

      const scheduleUpdate = () => {
        window.clearTimeout(updateTimer);
        updateTimer = window.setTimeout(submitUpdate, 420);
      };

      qsa('[data-cart-quantity]', form).forEach((input) => {
        input.addEventListener('change', () => {
          const value = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
          input.value = String(value);
          scheduleUpdate();
        });
      });

      qsa('[data-cart-stepper]', form).forEach((stepper) => {
        const input = qs('[data-cart-quantity]', stepper);
        if (!input) return;
        qs('[data-cart-minus]', stepper)?.addEventListener('click', () => {
          input.value = String(Math.max(0, (Number.parseInt(input.value || '0', 10) || 0) - 1));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        qs('[data-cart-plus]', stepper)?.addEventListener('click', () => {
          input.value = String((Number.parseInt(input.value || '0', 10) || 0) + 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  const initBackToTop = (root = document) => {
    qsa('[data-back-top]:not([data-back-bound])', root).forEach((button) => {
      button.dataset.backBound = 'true';
      button.addEventListener('click', () => window.scrollTo({
        top: 0,
        behavior: reducedMotion ? 'auto' : 'smooth'
      }));
    });
  };

  const init = (root = document) => {
    initReveals(root);
    initGalleries(root);
    initProductForms(root);
    initCart(root);
    initBackToTop(root);
  };

  init();

  // Shopify Theme Editor can reload a section without reloading the page.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
